import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { asArray } from "@/lib/arrays";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Users,
  MessageSquare,
  AlertCircle,
  Clock,
  CheckCircle,
  Send
} from "lucide-react";
import { toast } from "sonner";

const CounselorDashboard = () => {
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [response, setResponse] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ticketsRes, usersRes] = await Promise.all([
        axios.get(`${API}/support/tickets`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true })
      ]);
      setTickets(asArray(ticketsRes.data, "tickets"));
      setUsers(asArray(usersRes.data, "users"));
    } catch (error) {
      console.error("Error fetching data:", error); // eslint-disable-line no-console
    } finally {
      setLoading(false);
    }
  };

  const respondToTicket = async () => {
    if (!response.trim()) {
      toast.error("Please enter a response");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/support/tickets/${selectedTicket.ticket_id}/respond`,
        { message: response },
        { withCredentials: true }
      );
      toast.success("Response sent");
      setResponse("");
      setSelectedTicket(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to send response");
    } finally {
      setSubmitting(false);
    }
  };

  const updateTicketStatus = async (ticketId, status) => {
    try {
      await axios.put(
        `${API}/support/tickets/${ticketId}/status`,
        { status },
        { withCredentials: true }
      );
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "open":
        return <AlertCircle className="w-4 h-4" />;
      case "in_progress":
        return <Clock className="w-4 h-4" />;
      case "resolved":
      case "closed":
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "open":
        return "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800";
      case "in_progress":
        return "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800";
      case "resolved":
        return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800";
      case "closed":
        return "bg-slate-100 text-slate-700 border-slate-200 dark:bg-zinc-800 dark:text-zinc-300";
      default:
        return "bg-slate-100 text-slate-700 border-slate-200";
    }
  };

  const openTickets = tickets.filter((t) => t.status === "open");
  const inProgressTickets = tickets.filter((t) => t.status === "in_progress");

  if (loading) {
    return (
      <DashboardLayout title="Support Management">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Support Management">
      <div className="space-y-6" data-testid="counselor-dashboard">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Card className="bg-white dark:bg-[#131B2C] border-slate-200/80 dark:border-[#1E293B] shadow-xs" data-testid="stat-users">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/60 dark:text-blue-400 dark:border-blue-800 flex items-center justify-center shrink-0">
                  <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white font-outfit">{users.length}</p>
                  <p className="text-xs text-slate-500 dark:text-[#64748B]">Total Users</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-[#131B2C] border-slate-200/80 dark:border-[#1E293B] shadow-xs" data-testid="stat-total-tickets">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-400 dark:border-emerald-800 flex items-center justify-center shrink-0">
                  <MessageSquare className="w-5 h-5 text-emerald-600 dark:text-emerald-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white font-outfit">{tickets.length}</p>
                  <p className="text-xs text-slate-500 dark:text-[#64748B]">Total Tickets</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-[#131B2C] border-slate-200/80 dark:border-[#1E293B] shadow-xs" data-testid="stat-open-tickets">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/60 dark:text-amber-400 dark:border-amber-800 flex items-center justify-center shrink-0">
                  <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white font-outfit">{openTickets.length}</p>
                  <p className="text-xs text-slate-500 dark:text-[#64748B]">Open</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-[#131B2C] border-slate-200/80 dark:border-[#1E293B] shadow-xs" data-testid="stat-in-progress">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-950/60 dark:text-indigo-400 dark:border-indigo-800 flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-indigo-600 dark:text-indigo-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white font-outfit">{inProgressTickets.length}</p>
                  <p className="text-xs text-slate-500 dark:text-[#64748B]">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tickets List */}
        <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-[#1E293B]" data-testid="tickets-list">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-white font-outfit">Support Tickets</CardTitle>
          </CardHeader>
          <CardContent>
            {tickets.length === 0 ? (
              <p className="text-center text-gray-500 dark:text-[#64748B] py-8">No tickets</p>
            ) : (
              <div className="space-y-4">
                {tickets.map((ticket) => (
                  <div
                    key={ticket.ticket_id}
                    className="bg-gray-50 dark:bg-[#0A0E17] border border-gray-200 dark:border-[#1E293B] rounded-lg p-4"
                    data-testid={`ticket-${ticket.ticket_id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <Badge className={`${getStatusClass(ticket.status)} border text-xs font-medium rounded-full px-2.5 py-0.5`}>
                            {getStatusIcon(ticket.status)}
                            <span className="ml-1 capitalize">{ticket.status.replace("_", " ")}</span>
                          </Badge>
                          <Badge variant="outline" className="text-slate-600 dark:text-slate-300 border-slate-200 dark:border-zinc-700 text-xs rounded-full px-2.5 py-0.5">
                            {ticket.type}
                          </Badge>
                          <Badge className={`border text-xs font-medium rounded-full px-2.5 py-0.5 capitalize ${
                            ticket.priority === "high" 
                              ? "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800"
                              : ticket.priority === "medium"
                              ? "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800"
                              : "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800"
                          }`}>
                            {ticket.priority} priority
                          </Badge>
                        </div>
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-1 text-sm">{ticket.subject}</h3>
                        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 line-clamp-2">{ticket.description}</p>
                        <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-2 font-mono">
                          User: {ticket.user_id} • {new Date(ticket.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex flex-row sm:flex-col items-center sm:items-end gap-2 shrink-0 pt-2 sm:pt-0">
                        <Select
                          defaultValue={ticket.status}
                          onValueChange={(value) => updateTicketStatus(ticket.ticket_id, value)}
                        >
                          <SelectTrigger className="w-32 bg-white dark:bg-[#131B2C] border-slate-200 dark:border-[#1E293B] text-gray-900 dark:text-white text-xs rounded-xl h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white dark:bg-[#131B2C] border-slate-200 dark:border-[#1E293B]">
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          onClick={() => setSelectedTicket(ticket)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs rounded-xl shadow-xs transition-colors h-8 px-3.5"
                          data-testid={`respond-btn-${ticket.ticket_id}`}
                        >
                          Respond
                        </Button>
                      </div>
                    </div>
                    
                    {/* Responses */}
                    {ticket.responses && ticket.responses.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-[#1E293B]">
                        <p className="text-xs text-gray-500 dark:text-[#64748B] mb-2">Responses:</p>
                        {ticket.responses.map((resp, idx) => (
                          <div key={idx} className="bg-white dark:bg-[#131B2C] rounded p-3 mb-2">
                            <p className="text-sm text-gray-900 dark:text-white">{resp.message}</p>
                            <p className="text-xs text-gray-500 dark:text-[#64748B] mt-1">
                              {resp.responder_name} • {new Date(resp.created_at).toLocaleString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Response Dialog */}
        <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
          <DialogContent className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white">
            <DialogHeader>
              <DialogTitle className="font-outfit">Respond to Ticket</DialogTitle>
            </DialogHeader>
            {selectedTicket && (
              <div className="space-y-4 mt-4">
                <div className="bg-gray-50 dark:bg-[#0A0E17] rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-1">{selectedTicket.subject}</h4>
                  <p className="text-sm text-gray-500 dark:text-[#94A3B8]">{selectedTicket.description}</p>
                </div>
                <Textarea
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                  placeholder="Type your response..."
                  className="bg-gray-50 dark:bg-[#0A0E17] border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white min-h-[120px]"
                  data-testid="response-textarea"
                />
                <Button
                  onClick={respondToTicket}
                  disabled={submitting}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs rounded-xl shadow-xs transition-colors h-10"
                  data-testid="send-response-btn"
                >
                  {submitting ? "Sending..." : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Response
                    </>
                  )}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default CounselorDashboard;
